/**
 * Pure logic to analyze a competitor's situation.
 * Gated by the 4 support functions (marketing, commerce, admin/exploitation,
 * finance). Without those, returns a "locked" payload telling the UI to hide.
 *
 * With them, returns the full pedagogically-detailed analysis used by the M&A flow:
 *   - revenue / fleet / drivers / contracts / reputation
 *   - estimated equity & net margin
 *   - health score 0-100
 *   - DAF recommendation: 'ideal_target' | 'fair' | 'overpriced' | 'toxic'
 *   - dominant weakness used as negotiation leverage
 *
 * 12/02/2026 — Scaling dynamique retour user prod : les KPIs concurrent
 * sont multipliés par `computeMarketScale(...).competitorScale` afin de
 * rester alignés sur la progression du joueur (phase + flotte + jours).
 */

import { computeMarketScale } from './marketScale';

const REQUIRED_FAMILIES = ['marketing', 'commerce', 'admin', 'finance'];

/**
 * Helper — returns the set of *family ids* the player has hired at least one
 * role for.
 *
 * @param {Object} hiredSupport — state.hiredSupport (map roleId → {hiredAt})
 * @param {Array}  supportRoles — full SUPPORT_ROLES catalogue (for familyId lookup)
 */
export function hiredFamilies(hiredSupport, supportRoles) {
  const families = new Set();
  Object.keys(hiredSupport || {}).forEach(roleId => {
    const role = (supportRoles || []).find(r => r.id === roleId);
    if (role?.familyId) families.add(role.familyId);
  });
  return families;
}

export function isCompetitionAnalysisUnlocked(hiredSupport, supportRoles) {
  const families = hiredFamilies(hiredSupport, supportRoles);
  return REQUIRED_FAMILIES.every(f => families.has(f));
}

export function missingFamiliesForAnalysis(hiredSupport, supportRoles) {
  const families = hiredFamilies(hiredSupport, supportRoles);
  return REQUIRED_FAMILIES.filter(f => !families.has(f));
}

/**
 * Project a competitor's current KPIs based on age and growth rate.
 * Uses a deterministic, day-based simulation so the analysis is stable
 * across re-renders (no random jitter every tick).
 *
 * 12/02/2026 — Scaling par état du joueur : `scale` (issu de
 * `computeMarketScale`) multiplie CA, flotte, chauffeurs pour maintenir
 * la pertinence des concurrents en mid/late game.
 */
function projectCompetitor(competitor, totalDays = 0, scale = 1) {
  const months = Math.max(0, Math.floor(totalDays / 30));
  const growth = Math.pow(1 + (competitor.growthRate || 0), months);
  const ca = Math.round((competitor.baseCa || 0) * growth * scale);
  const fleet = Math.round((competitor.baseFleet || 0) * Math.pow(1 + (competitor.growthRate || 0) * 0.5, months) * scale);
  const drivers = Math.round((competitor.baseDrivers || 0) * Math.pow(1 + (competitor.growthRate || 0) * 0.5, months) * scale);
  // Reputation slowly converges towards 75 (or stays at base if higher).
  const target = Math.max(competitor.baseReputation || 50, 75);
  const reputation = Math.round((competitor.baseReputation || 50) + (target - (competitor.baseReputation || 50)) * Math.min(1, months / 36));
  return { ca, fleet, drivers, reputation };
}

/**
 * Estimate financial KPIs from the projected operational data and the
 * competitor personality (agressif/corporate/ecolo influence margins).
 */
function estimateFinancials(competitor, projected) {
  const personality = competitor.personality || 'corporate';
  // Net margin assumptions per personality (pedagogically realistic)
  const marginByPersonality = {
    agressif: 0.04,   // volume sur prix bas → marge réduite
    corporate: 0.10,  // gros groupe, marge confortable
    ecolo: 0.06,      // investissements verts pèsent sur la marge
  };
  const netMargin = marginByPersonality[personality] ?? 0.07;
  const monthlyNetProfit = projected.ca * netMargin;
  // Equity proxy: 12 mois de bénéfice net retenu × ancienneté (>= 1 an)
  const months = 24; // hypothèse : entreprise mature en place depuis 2 ans
  const equity = Math.round(monthlyNetProfit * months);
  // Active contracts: échelle proportionnelle à la flotte
  const contractsActive = Math.max(1, Math.round(projected.fleet * 1.5));
  // Net debt: 1.2× CA mensuel (charge financière correcte)
  const netDebt = Math.round(projected.ca * 1.2);
  return { netMargin, monthlyNetProfit: Math.round(monthlyNetProfit), equity, contractsActive, netDebt };
}

/**
 * Synthetic health score (0..100), used by the DAF recommendation.
 * Aggregates 4 axes: cash runway, reputation, growth, debt ratio.
 */
function computeHealthScore(competitor, projected, financials) {
  const runwayScore = financials.equity > 0 ? Math.min(100, (financials.equity / financials.netDebt) * 100) : 0;
  const repScore = projected.reputation;
  const growthScore = Math.min(100, (competitor.growthRate || 0) * 1000); // 5%→50, 10%→100
  const debtScore = Math.max(0, 100 - (financials.netDebt / Math.max(1, projected.ca * 12)) * 100);
  return Math.round(runwayScore * 0.25 + repScore * 0.35 + growthScore * 0.20 + debtScore * 0.20);
}

/**
 * Find the dominant weakness the player can leverage in negotiation.
 * Returned as a tag + a human-readable label.
 */
function detectDominantWeakness(competitor, projected, financials) {
  const issues = [];
  if (financials.netDebt > projected.ca * 18) {
    issues.push({ tag: 'debt', label: "Endettement élevé (dette > 18 mois de CA)", leverage: 0.05 });
  }
  if (projected.reputation < 50) {
    issues.push({ tag: 'reputation', label: "Réputation dégradée (< 50)", leverage: 0.04 });
  }
  if (financials.netMargin < 0.05) {
    issues.push({ tag: 'margin', label: "Marge nette faible (< 5 %)", leverage: 0.04 });
  }
  if (projected.fleet > 0 && (competitor.growthRate || 0) < 0.02) {
    issues.push({ tag: 'stagnation', label: "Croissance < 2 %/an (stagnation)", leverage: 0.03 });
  }
  if (issues.length === 0) {
    return { tag: 'none', label: "Aucune faiblesse exploitable détectée", leverage: 0 };
  }
  // Return the highest-leverage issue
  return issues.sort((a, b) => b.leverage - a.leverage)[0];
}

/**
 * DAF recommendation tier based on health score + price/CA ratio.
 */
function dafRecommendation(healthScore) {
  if (healthScore >= 65) return { tier: 'ideal_target', label: 'Cible idéale', tone: 'emerald' };
  if (healthScore >= 45) return { tier: 'fair', label: 'Cible correcte', tone: 'amber' };
  if (healthScore >= 25) return { tier: 'overpriced', label: 'Cible risquée', tone: 'orange' };
  return { tier: 'toxic', label: 'Cible toxique', tone: 'rose' };
}

/**
 * Public — returns the full analysis (or a `locked: true` placeholder).
 *
 * 12/02/2026 — Accepte facultativement `playerState` (phase, vehicles,
 * totalDays) pour appliquer un scaling dynamique aux KPIs concurrent.
 * Sans playerState → scale = 1 (rétro-compat).
 */
export function analyzeCompetitor(competitor, { hiredSupport, supportRoles, totalDays = 0, playerState = null }) {
  if (!isCompetitionAnalysisUnlocked(hiredSupport, supportRoles)) {
    return {
      locked: true,
      missing: missingFamiliesForAnalysis(hiredSupport, supportRoles),
    };
  }
  const scaleInfo = playerState
    ? computeMarketScale({
      phase: playerState.phase || 1,
      fleetSize: (playerState.vehicles || []).length,
      totalDays: playerState.totalDays || totalDays,
    })
    : { competitorScale: 1, phaseBoost: 0, fleetBoost: 0, daysBoost: 0 };
  const projected = projectCompetitor(competitor, totalDays, scaleInfo.competitorScale);
  const financials = estimateFinancials(competitor, projected);
  const healthScore = computeHealthScore(competitor, projected, financials);
  const weakness = detectDominantWeakness(competitor, projected, financials);
  const recommendation = dafRecommendation(healthScore);
  return {
    locked: false,
    competitorId: competitor.id,
    name: competitor.name,
    color: competitor.color,
    avatar: competitor.avatar,
    specialties: competitor.specialties || [],
    ...projected,
    ...financials,
    healthScore,
    weakness,
    recommendation,
    // 12/02/2026 — Trace le scale appliqué pour l'UI/debug.
    marketScale: scaleInfo.competitorScale,
    marketScaleBreakdown: {
      phaseBoost: scaleInfo.phaseBoost,
      fleetBoost: scaleInfo.fleetBoost,
      daysBoost: scaleInfo.daysBoost,
    },
  };
}
